import { noticeLabelKeys } from '@lib/spproject/inspectionStatus';
import { type Inspection } from '@lib/spproject/types';
import {
  type ReportCheckpointsSegment, type ReportChromeSlot, type ReportSummarySegment,
} from '@store/api/reportDesignEndpoints';

/**
 * The choices the report designer offers outside the block catalog: the templates of the two
 * fixed sections, what a header or footer slot can hold, the statuses a list can be narrowed
 * to. Every `label` is a key of the localization CMS.
 */

/**
 * The accent a report is drawn in while its design names none: `palette.accent` of the
 * backend's renderer, repeated here only to show it in the colour box.
 */
export const DEFAULT_ACCENT = '#2D6CDF';

/** The statuses in the order every summary of a report lists them. */
export const STATUSES: { value: Inspection.NoticeType; label: string }[] =
  (['Ok', 'Warning', 'Error', 'NotChecked'] as const).map((value) => ({ value, label: noticeLabelKeys[value] }));

export const SLOT_LABELS: Record<ReportChromeSlot['type'], string> = {
  none: 'ReportDesign_Slot_None',
  logo: 'ReportDesign_Logo',
  brandName: 'ReportDesign_Field_BrandName',
  text: 'ReportDesign_Field_Text',
  date: 'Report_Generated_Date',
  pageNumbers: 'ReportDesign_Slot_PageNumbers',
};

export const SUMMARY_TEMPLATES: { value: ReportSummarySegment['template']; label: string }[] = [
  { value: 'dashboard', label: 'ReportDesign_Template_Dashboard' },
  { value: 'table', label: 'ReportDesign_Template_Table' },
  { value: 'minimal', label: 'ReportDesign_Template_Minimal' },
  { value: 'none', label: 'ReportDesign_Template_None' },
];

export const CHECKPOINT_TEMPLATES: { value: ReportCheckpointsSegment['template']; label: string }[] = [
  { value: 'cards', label: 'ReportDesign_Template_Cards' },
  { value: 'grid', label: 'ReportDesign_Template_Grid' },
  { value: 'table', label: 'ReportDesign_Template_Table' },
  { value: 'detailed', label: 'ReportDesign_Template_Detailed' },
  { value: 'none', label: 'ReportDesign_Template_None' },
];

/**
 * What each checkpoints template reads of the segment. A template ignores the rest, so the
 * inspector offers only these: a switch that changes nothing on the page is a question the
 * author should not be asked.
 */
export const CHECKPOINT_OPTIONS: Record<ReportCheckpointsSegment['template'], readonly string[]> = {
  cards: ['title', 'filter', 'photos', 'comments', 'customFields'],
  grid: ['title', 'filter', 'comments'],
  table: ['title', 'filter', 'descriptions', 'dates'],
  detailed: ['filter', 'photos', 'comments', 'customFields'],
  none: [],
};

export const SIDES = [
  { side: 'left', label: 'Common_Left' },
  { side: 'center', label: 'ReportDesign_Option_Center' },
  { side: 'right', label: 'Common_Right' },
] as const;
